'use client'

import React, { useState } from 'react'

import { CMSLink } from '@/theme/components/CMSLink'
import { Media } from '@/theme/components/Media/Media'
import type { CardsGroupBlock as CardsGroupProps } from '@/payload-types'

/**
 * Un onglet par catégorie (Femme / Homme / Enfant…), une seule liste de
 * tarifs affichée à la fois dans le "ticket" : évite d'empiler 7-8 cartes à
 * la suite et reprend le même pattern de navigation que les filtres de la
 * galerie, pour aller droit à la catégorie qui intéresse.
 */
export const PricingCards: React.FC<CardsGroupProps> = ({ sectionTitle, subtitle, cards }) => {
  const [activeIndex, setActiveIndex] = useState(0)

  if (!cards || cards.length === 0) return null

  const activeItem = cards[activeIndex] ?? cards[0]

  return (
    <>
      <div className="section-head centered">
        <p className="eyebrow">Tarifs</p>
        {sectionTitle && <h2>{sectionTitle}</h2>}
        {subtitle && <p className="mt-3 mx-auto">{subtitle}</p>}
      </div>

      {cards.length > 1 && (
        <div className="pricing-tabs">
          {cards.map((card, idx) => (
            <button
              key={idx}
              type="button"
              className={`pricing-tab${idx === activeIndex ? ' active' : ''}`}
              onClick={() => setActiveIndex(idx)}
            >
              {card.title}
            </button>
          ))}
        </div>
      )}

      {activeItem && (
        <div
          key={activeIndex}
          className="pricing-card flex flex-col min-[861px]:flex-row items-start gap-[18px] min-[861px]:gap-7"
        >
          {activeItem.media && typeof activeItem.media === 'object' ? (
            <Media
              resource={activeItem.media}
              imgClassName="w-full h-[200px] object-cover rounded-lg min-[861px]:h-[280px] min-[861px]:w-[220px] min-[861px]:shrink-0 min-[861px]:sticky min-[861px]:top-7"
              htmlElement={null}
            />
          ) : (
            activeItem.title && (
              <img
                className="w-full h-[200px] object-cover rounded-lg min-[861px]:h-[280px] min-[861px]:w-[220px] min-[861px]:shrink-0 min-[861px]:sticky min-[861px]:top-7"
                src={`https://placehold.co/400x400?text=${encodeURIComponent(activeItem.title)}`}
                alt={activeItem.title}
              />
            )
          )}
          <div className="flex flex-1 min-w-0 flex-col gap-[18px]">
            <div>
              <h3>{activeItem.title}</h3>
              {activeItem.description && <p className="mt-1">{activeItem.description}</p>}
            </div>
            <div className="pricing-tear" />
            {activeItem.listElements && activeItem.listElements.length > 0 && (
              <ul
                className={`pricing-list${activeItem.listElements.length >= 8 ? ' pricing-list--columns' : ''}`}
              >
                {activeItem.listElements.map((el, i) => (
                  <li key={i}>
                    <span>{el.element}</span>
                    <span>{el.price}</span>
                  </li>
                ))}
              </ul>
            )}
            {activeItem.links?.[0]?.link && (
              <CMSLink {...activeItem.links[0].link} appearance="outline" />
            )}
          </div>
        </div>
      )}
    </>
  )
}
